import { applyCanvasOverlays, cssFilterFromSettings } from "./filters";

export const DEFAULT_ADJUSTMENTS = {
  // Light
  exposure:   0,
  brightness: 0,
  contrast:   0,
  highlights: 0,
  shadows:    0,
  whites:     0,
  blacks:     0,
  // Color
  temperature: 0,
  tint:        0,
  vibrance:    0,
  saturation:  0,
  // Detail
  sharpness:      0,
  clarity:        0,
  dehaze:         0,
  noiseReduction: 0,
  grain:          0,
  // Effects
  vignette: 0,
  fade:     0,
  blur:     0,
  texture:  0,
};

export async function renderEditedBlob(blob, {
  adjustments = {},
  rotation   = 0,
  straighten = 0,
  flipX      = false,
  flipY      = false,
  text       = "",
  textSize   = 2,
  textPos    = "bottom",
  showDate   = false,
  frame      = "none",
} = {}) {
  const bitmap = await createImageBitmap(blob);
  const totalRot = rotation + straighten;
  const quarterTurns = (((Math.round(rotation / 90)) % 4) + 4) % 4;
  const rotated = quarterTurns % 2 === 1;

  // Canvas sized for 90° rotation swapping
  const canvas = document.createElement("canvas");
  canvas.width  = rotated ? bitmap.height : bitmap.width;
  canvas.height = rotated ? bitmap.width  : bitmap.height;
  const ctx = canvas.getContext("2d", { alpha: false });

  // Build a combined CSS filter for export
  const bright = adjustments.brightness + (adjustments.exposure || 0) * 0.4
    + (adjustments.whites || 0) * 0.4 - Math.abs(adjustments.blacks || 0) * 0.28;
  const cont   = adjustments.contrast + (adjustments.clarity || 0) * 0.35 + (adjustments.dehaze || 0) * 0.22;
  const sat    = adjustments.saturation + (adjustments.vibrance || 0) * 0.55 + (adjustments.dehaze || 0) * 0.18;
  const exportAdj = { ...adjustments, brightness: bright, contrast: cont, saturation: sat };

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((totalRot * Math.PI) / 180);
  ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  ctx.filter = cssFilterFromSettings(exportAdj);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  ctx.restore();
  ctx.filter = "none";

  // Canvas overlays (vignette, grain)
  applyCanvasOverlays(ctx, canvas.width, canvas.height, exportAdj);

  // Fade overlay (lift blacks slightly)
  const fade = Math.max(0, adjustments.fade || 0);
  if (fade > 0) {
    const alpha = fade / 250;
    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Frame
  if (frame === "white" || frame === "polaroid") {
    const pad = frame === "polaroid" ? Math.round(canvas.width * 0.055) : Math.round(canvas.width * 0.038);
    const bot = frame === "polaroid" ? pad * 3.2 : pad;
    const tmp = document.createElement("canvas");
    tmp.width  = canvas.width  + pad * 2;
    tmp.height = canvas.height + pad + bot;
    const tc = tmp.getContext("2d", { alpha: false });
    tc.fillStyle = "#fff";
    tc.fillRect(0, 0, tmp.width, tmp.height);
    tc.drawImage(canvas, pad, pad);
    canvas.width  = tmp.width;
    canvas.height = tmp.height;
    ctx.drawImage(tmp, 0, 0);
  } else if (frame === "black") {
    const pad = Math.round(canvas.width * 0.038);
    const tmp = document.createElement("canvas");
    tmp.width  = canvas.width  + pad * 2;
    tmp.height = canvas.height + pad * 2;
    const tc = tmp.getContext("2d", { alpha: false });
    tc.fillStyle = "#111";
    tc.fillRect(0, 0, tmp.width, tmp.height);
    tc.drawImage(canvas, pad, pad);
    canvas.width  = tmp.width;
    canvas.height = tmp.height;
    ctx.drawImage(tmp, 0, 0);
  } else if (frame === "film") {
    const pad = Math.round(canvas.width * 0.028);
    const tmp = document.createElement("canvas");
    tmp.width  = canvas.width  + pad * 2;
    tmp.height = canvas.height + pad * 2;
    const tc = tmp.getContext("2d", { alpha: false });
    tc.fillStyle = "#222";
    tc.fillRect(0, 0, tmp.width, tmp.height);
    tc.fillStyle = "#1a1a1a";
    const sprocket = 8, sprW = Math.round(pad * 0.7), sprH = sprW * 1.3;
    for (let y = sprocket; y < tmp.height - sprH; y += sprocket * 2.8) {
      tc.fillStyle = "#444";
      tc.roundRect(pad * 0.15, y, sprW, sprH, 2);
      tc.fill();
      tc.fillStyle = "#444";
      tc.roundRect(tmp.width - pad * 0.15 - sprW, y, sprW, sprH, 2);
      tc.fill();
    }
    tc.drawImage(canvas, pad, pad);
    canvas.width  = tmp.width;
    canvas.height = tmp.height;
    ctx.drawImage(tmp, 0, 0);
  }

  // Text overlay
  const textContent = (text || "").trim();
  if (textContent) {
    const sizes = [0.028, 0.036, 0.048];
    const fontSize = Math.max(28, canvas.width * (sizes[textSize - 1] || sizes[1]));
    const padding  = fontSize * 1.6;
    const yPositions = { top: padding, center: canvas.height / 2, bottom: canvas.height - padding };
    const y = yPositions[textPos] || yPositions.bottom;
    ctx.fillStyle = "rgba(0,0,0,.48)";
    ctx.fillRect(28, y - fontSize * 0.8, canvas.width - 56, fontSize * 1.7);
    ctx.fillStyle = "#fff";
    ctx.font = `${fontSize}px system-ui`;
    ctx.fillText(textContent.slice(0, 90), 44, y + fontSize * 0.42);
  }

  // Date stamp
  if (showDate) {
    const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    const fs = Math.max(18, canvas.width * 0.022);
    ctx.fillStyle = "rgba(0,0,0,.46)";
    ctx.fillRect(canvas.width - fs * 8 - 16, canvas.height - fs * 2 - 16, fs * 8, fs * 1.8);
    ctx.fillStyle = "rgba(255,255,255,.85)";
    ctx.font = `${fs}px system-ui`;
    ctx.fillText(dateStr, canvas.width - fs * 8 - 8, canvas.height - fs * 0.7 - 16);
  }

  bitmap.close?.();
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}
